import { SquareIconButton } from '@components/SquareIconButton'
import { Button, Stack, styled, Typography } from '@mui/material'
import FeatherIcon from 'feather-icons-react'
import { useState } from 'react'
import {
  type EditorChangeRequest,
  EditRelationOperation,
  type Path,
  type Relation,
  type RelationTemplate,
  type RelationUpdateRequest,
} from '../api/api'
import { RelationSkeleton } from '../components/RelationSkeleton'

interface RelationEditorProps {
  onChange: (changeRequests: EditorChangeRequest[]) => void
  relation: Relation
  templates: RelationTemplate[]
  requests: EditorChangeRequest[]
}

interface Navigator {
  back: () => void
}

export interface RelationDetailsProps {
  relation: Relation | undefined
  templates: RelationTemplate[] | undefined
  onSaveChanges: (changeRequests: RelationUpdateRequest[]) => Promise<void>
  navigator: Navigator
  isLoading: boolean
  slots: { Editor: React.ComponentType<RelationEditorProps> }
}

export const RelationDetails = ({ relation, templates, onSaveChanges, navigator, slots: { Editor }, isLoading }: RelationDetailsProps) => {
  const [changeRequests, setChangeRequests] = useState<EditorChangeRequest[]>([])

  if (!relation || !templates || isLoading) return <RelationSkeleton />

  const handleSaveChanges = async () => {
    await onSaveChanges?.(
      changeRequests.map<RelationUpdateRequest>((request) => {
        switch (request.op) {
          case EditRelationOperation.ADD:
          case EditRelationOperation.REMOVE:
            return {
              op: request.op,
              templateId: request.relative.id,
            }
          case EditRelationOperation.ADD_ALLOWED_PATH:
          case EditRelationOperation.REMOVE_ALLOWED_PATH:
            return {
              op: request.op,
              from: request.path.from,
              to: request.path.to,
            }
        }
      }),
    )

    setChangeRequests([])
  }

  return (
    <Stack
      sx={{
        flex: 1,
        gap: 2,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          alignItems: 'baseline',
        }}
      >
        <SquareIconButton data-testid="go-back" onClick={navigator.back}>
          <FeatherIcon icon="chevron-left" />
        </SquareIconButton>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h3">{relation.name}</Typography>
        <Button variant="contained" disabled={changeRequests.length === 0} onClick={handleSaveChanges}>
          Save changes
        </Button>
      </Stack>
      <Container
        sx={{
          gap: 2,
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <Stack
          sx={{
            py: 2,
          }}
        >
          <Editor onChange={setChangeRequests} relation={relation} templates={templates} requests={changeRequests} />
        </Stack>
      </Container>
    </Stack>
  )
}

const Container = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}))
